import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api from '../utils/api';

const SPACE_ICONS = ['🚀','💡','⚡','🎯','🔧','📊','🌐','🛡','💎','🔬','📡','🏗','🔭','🛰','🌍'];

export default function SpacePage() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const ctx = useOutletContext();
  const currentUser = ctx?.user || {};
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const [pmModal, setPmModal] = useState(null); // {projectId, projectName}
  const [pendingAdd, setPendingAdd] = useState([]); // user ids to add
  const [pendingRemove, setPendingRemove] = useState([]); // user ids to remove
  const [pms, setPms] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [pmLoading, setPmLoading] = useState(false);

  async function openPmModal(sp) {
    setPendingAdd([]);
    setPendingRemove([]);
    setPmModal({projectId: sp.id, projectName: sp.name});
    setPmLoading(true);
    try {
      const [pmList, users] = await Promise.all([
        api.get(`/projects/${sp.id}/pms`),
        api.get('/users')
      ]);
      setPms(pmList || []);
      setAllUsers(users || []);
    } catch(e) { setPms([]); setAllUsers([]); }
    setPmLoading(false);
  }

  async function addPm(userId) {
    if (!pmModal) return;
    try {
      await api.post(`/projects/${pmModal.projectId}/pms`, { user_id: userId });
      const updated = await api.get(`/projects/${pmModal.projectId}/pms`);
      setPms(updated || []);
    } catch(e) { setSpaceError(e?.error||'Failed to add PM'); }
  }

  async function removePm(userId) {
    if (!pmModal) return;
    try {
      await api.delete(`/projects/${pmModal.projectId}/pms/${userId}`);
      const updated = await api.get(`/projects/${pmModal.projectId}/pms`);
      setPms(updated || []);
    } catch(e) { setSpaceError(e?.error||'Failed to remove PM'); }
  }

  async function savePmChanges() {
    if (!pmModal) return;
    try {
      for (const uid of pendingAdd) {
        await api.post(`/projects/${pmModal.projectId}/pms`, { user_id: uid });
      }
      for (const uid of pendingRemove) {
        await api.delete(`/projects/${pmModal.projectId}/pms/${uid}`);
      }
      const updated = await api.get(`/projects/${pmModal.projectId}/pms`);
      setPms(updated || []);
      setPendingAdd([]);
      setPendingRemove([]);
      setPmModal(null);
    } catch(e) { setSpaceError(e?.error||'Error saving changes'); }
  }

  function cancelPmModal() {
    setPendingAdd([]);
    setPendingRemove([]);
    setPmModal(null);
  }

  // Apply immediately so the person jumps to "Current Project Managers" right
  // away (better UX than staging + Save). Modal stays open.
  async function stagePmAdd(userId) {
    if (!pmModal) return;
    try {
      await api.post(`/projects/${pmModal.projectId}/pms`, { user_id: userId });
      const updated = await api.get(`/projects/${pmModal.projectId}/pms`);
      setPms(updated || []);
      loadSpaces();
    } catch(e) { setSpaceError(e?.error||'Could not add this Project Manager.'); }
  }

  async function stagePmRemove(userId) {
    if (!pmModal) return;
    try {
      await api.delete(`/projects/${pmModal.projectId}/pms/${userId}`);
      const updated = await api.get(`/projects/${pmModal.projectId}/pms`);
      setPms(updated || []);
      loadSpaces();
    } catch(e) { setSpaceError(e?.error||'Could not remove this Project Manager.'); }
  }

  useEffect(() => { loadSpaces(); }, []);
  // ── Silent background refresh for projects list ───────────────────────────
  useEffect(()=>{
    let lastCheck=Date.now(), active=true;
    const poll=async()=>{
      if(!active) return;
      // Just reload spaces every 20s to pick up new projects/renames
      try { await loadSpaces(); } catch(_){}
    };
    const iv=setInterval(poll,20000);
    return()=>{active=false;clearInterval(iv);};
  },[]);
  useEffect(() => { if (ctx?.spaces) setSpaces(ctx.spaces); }, [ctx?.spaces]);

  const [deleteConfirm, setDeleteConfirm] = React.useState(null);
  const [permError, setPermError] = React.useState(null);
  const [visConfirm, setVisConfirm] = React.useState(null); // {sp, target}
  const [scheduleModal, setScheduleModal] = React.useState(null); // sp being scheduled
  const [spaceError, setSpaceError] = React.useState(null);

  async function loadSpaces() {
    setLoading(true);
    try { const data = await api.get('/spaces'); setSpaces(Array.isArray(data)?data:[]); }
    catch(e) { setSpaces([]); } finally { setLoading(false); }
  }

  async function deleteSpace(sp) {
    setDeleteConfirm(sp);
  }

  async function confirmDelete() {
    const sp = deleteConfirm;
    setDeleteConfirm(null);
    try { await api.delete('/spaces/'+sp.id); await loadSpaces(); if(ctx?.reloadSpaces)ctx.reloadSpaces(); }
    catch(e) { setPermError(e?.error||'You do not have permission to delete this project.'); }
  }

  async function exportPortfolio() {
    try {
      const data = await api.get('/portfolio/export-data');
      const { buildPortfolioWorkbook } = await import('../utils/portfolioWorkbook');
      await buildPortfolioWorkbook(data);
    } catch(e) {
      setPermError(e?.error || 'Could not generate the portfolio export.');
    }
  }

  async function renameSpace(sp, newName) {
    if (!newName||newName.trim()===sp.name) return;
    try { await api.put('/spaces/'+sp.id,{name:newName.trim()}); await loadSpaces(); if(ctx?.reloadSpaces)ctx.reloadSpaces(); }
    catch(e) { setPermError(e?.error||'You do not have permission to rename this project.'); }
  }

  // Ask for confirmation first (changing visibility affects who can see the project)
  function requestVisibility(sp) {
    const target = (sp.visibility === 'public') ? 'private' : 'public';
    setVisConfirm({ sp, target });
  }
  async function confirmVisibility() {
    const { sp, target } = visConfirm;
    setVisConfirm(null);
    try {
      await api.put('/spaces/'+sp.id, { visibility: target });
      await loadSpaces();
      if (ctx?.reloadSpaces) ctx.reloadSpaces();
    } catch(e) {
      setPermError(e?.error||'You do not have permission to change this project\u2019s visibility.');
    }
  }

  if (loading&&spaces.length===0) return (
    <div className="page-enter" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)',gap:'10px'}}>
      <div style={{width:16,height:16,border:'2px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
      Loading projects…
    </div>
  );

  return (
    <div style={{flex:1,overflowY:'auto',padding:'32px 36px',background:'var(--bg)'}}>
      <div style={{marginBottom:'28px',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'16px'}}>
        <div>
          <h1 style={{fontSize:'24px',fontWeight:'800',color:'var(--text)',letterSpacing:'-0.6px',marginBottom:'4px'}}>Portfolio</h1>
          <p style={{color:'var(--muted)',fontSize:'13px'}}>{spaces.length} project{spaces.length!==1?'s':''} </p>
        </div>
        {(isSuperAdmin || currentUser?.role==='admin') && spaces.length>0 && (
          <button onClick={exportPortfolio} title="Export a summary workbook across all projects"
            style={{padding:'9px 16px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700',color:'var(--text)',whiteSpace:'nowrap'}}>
            📊 Export Portfolio (Excel)
          </button>
        )}
      </div>
      {spaces.length===0 ? (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'80px 20px',gap:'16px'}}>
          <div style={{width:'72px',height:'72px',borderRadius:'20px',background:'var(--brand-dim)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'32px'}}>🚀</div>
          <h3 style={{fontSize:'17px',fontWeight:'700',color:'var(--text)'}}>No projects yet</h3>
          <p style={{fontSize:'13px',color:'var(--muted)',textAlign:'center',maxWidth:'300px',lineHeight:1.6}}>Click the <strong>+</strong> button next to Projects in the sidebar</p>
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:'18px'}}>
          {spaces.map((sp,idx)=>(
            <ProjectCard key={sp.id} sp={sp} icon={SPACE_ICONS[idx%SPACE_ICONS.length]}
              onNavigate={navigate}
              isOwner={isSuperAdmin||sp.owner_id===ctx?.user?.id}
              onDelete={()=>deleteSpace(sp)}
              onRename={(n)=>renameSpace(sp,n)}
              onToggleVisibility={(isSuperAdmin||sp.owner_id===ctx?.user?.id)?()=>requestVisibility(sp):null}
              onManagePMs={(isSuperAdmin||sp.owner_id===ctx?.user?.id)?()=>openPmModal(sp):null}
              onSchedule={(isSuperAdmin||sp.owner_id===ctx?.user?.id)?()=>setScheduleModal(sp):null}/>
          ))}
        </div>
      )}
      {/* General error toast */}
      {spaceError&&(
        <div style={{position:'fixed',bottom:'24px',left:'50%',transform:'translateX(-50%)',zIndex:9999,background:'#1E1E2E',color:'#fff',padding:'12px 24px',borderRadius:'12px',fontSize:'13px',fontWeight:'600',boxShadow:'0 8px 32px rgba(0,0,0,0.3)',display:'flex',alignItems:'center',gap:'12px',maxWidth:'480px'}}>
          <span style={{fontSize:'16px'}}>⚠️</span>
          <span style={{flex:1}}>{spaceError}</span>
          <button onClick={()=>setSpaceError(null)} style={{background:'none',border:'none',color:'rgba(255,255,255,0.6)',cursor:'pointer',fontSize:'16px',padding:'0',lineHeight:1}}>✕</button>
        </div>
      )}

      {/* Permission error modal */}
      {permError&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'420px',textAlign:'center'}}>
            <div style={{fontSize:'36px',marginBottom:'12px'}}>🔒</div>
            <h3 style={{fontSize:'16px',fontWeight:'700',color:'var(--text)',marginBottom:'12px'}}>Permission Denied</h3>
            <p style={{fontSize:'14px',color:'var(--muted)',marginBottom:'24px',lineHeight:1.6}}>{permError}</p>
            <button className="btn btn-primary" onClick={()=>setPermError(null)}>OK</button>
          </div>
        </div>
      )}

      {deleteConfirm&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'420px',textAlign:'center'}}>
            <div style={{fontSize:'36px',marginBottom:'12px'}}>🗑️</div>
            <h3 style={{fontSize:'16px',fontWeight:'700',color:'var(--text)',marginBottom:'8px'}}>Delete Project</h3>
            <p style={{fontSize:'14px',color:'var(--muted)',marginBottom:'6px'}}>You are about to permanently delete <strong style={{color:'var(--text)'}}>{deleteConfirm.name}</strong></p>
            <p style={{fontSize:'12px',color:'#DC2626',fontWeight:'600',marginBottom:'24px',padding:'10px',background:'#FEF2F2',borderRadius:'8px',border:'1px solid #FECACA'}}>⚠ This will delete ALL phases, lists, tasks and data. This cannot be undone.</p>
            <div style={{display:'flex',gap:'10px',justifyContent:'center'}}>
              <button className="btn" style={{minWidth:'100px'}} onClick={()=>setDeleteConfirm(null)}>Cancel</button>
              <button className="btn" style={{minWidth:'100px',background:'#DC2626',color:'#fff',border:'none'}} onClick={confirmDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {visConfirm&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'500px',maxWidth:'94vw',textAlign:'center'}}>
            <div style={{fontSize:'36px',marginBottom:'12px'}}>{visConfirm.target==='public'?'🌐':'🔒'}</div>
            <h3 style={{fontSize:'16px',fontWeight:'700',color:'var(--text)',marginBottom:'8px'}}>
              {visConfirm.target==='public'?'Make this project Public?':'Make this project Private?'}
            </h3>
            <p style={{fontSize:'14px',color:'var(--muted)',marginBottom:'14px'}}>
              <strong style={{color:'var(--text)'}}>{visConfirm.sp.name}</strong>
            </p>
            <div style={{fontSize:'13px',color:'var(--muted)',marginBottom:'22px',padding:'14px 16px',
              background:visConfirm.target==='public'?'rgba(28,166,107,0.08)':'rgba(224,85,107,0.08)',
              borderRadius:'8px',border:'1px solid '+(visConfirm.target==='public'?'rgba(28,166,107,0.25)':'rgba(224,85,107,0.25)'),
              lineHeight:1.65,textAlign:'left'}}>
              {visConfirm.target==='public' ? (
                <>
                  Every workspace member will be able to <strong style={{color:'var(--text)'}}>see this project in read-only mode</strong>.<br/>
                  People you invite as members keep their full access, and private phases inside it stay hidden.
                </>
              ) : (
                <>
                  This project will <strong style={{color:'var(--text)'}}>disappear for anyone who could only see it because it was public</strong>.<br/>
                  Still keep access: <strong style={{color:'var(--text)'}}>the owner, invited members, Project Managers, and the Super Admin</strong>.<br/>
                  No data is lost — you can switch it back anytime.
                </>
              )}
            </div>
            <div style={{display:'flex',gap:'10px',justifyContent:'center'}}>
              <button className="btn" style={{padding:'9px 22px'}} onClick={()=>setVisConfirm(null)}>Cancel</button>
              <button className="btn btn-primary" style={{padding:'9px 22px'}} onClick={confirmVisibility}>
                {visConfirm.target==='public'?'Make Public':'Make Private'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Schedule Modal */}
      {scheduleModal && (
        <ProjectScheduleModal sp={scheduleModal} onClose={()=>setScheduleModal(null)} onSaved={()=>{ setScheduleModal(null); load(); }} />
      )}

      {/* PM Management Modal */}
      {pmModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'var(--surface)',borderRadius:'16px',padding:'28px',width:'520px',maxWidth:'100%',maxHeight:'85vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}}>
              <div>
                <div style={{fontSize:'16px',fontWeight:'800',color:'var(--text)'}}>👑 Project Managers</div>
                <div style={{fontSize:'12px',color:'var(--muted)',marginTop:'2px'}}>{pmModal.projectName}</div>
              </div>
              <button onClick={cancelPmModal} style={{background:'none',border:'none',cursor:'pointer',fontSize:'20px',color:'var(--muted)',lineHeight:1}}>✕</button>
            </div>
            {pmLoading ? (
              <div style={{textAlign:'center',padding:'30px',color:'var(--muted)'}}>Loading…</div>
            ) : (
              <>
                <div style={{marginBottom:'20px'}}>
                  <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'10px'}}>Current Project Managers</div>
                  {pms.length===0
                    ? <div style={{fontSize:'13px',color:'var(--muted)',fontStyle:'italic',padding:'8px 0'}}>No project managers assigned yet</div>
                    : pms.map(pm=>(
                      <div key={pm.user_id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 12px',background:'var(--surface2)',borderRadius:'10px',marginBottom:'6px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                          <div style={{width:34,height:34,borderRadius:'50%',background:pm.avatar_color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:'12px',fontWeight:'700',flexShrink:0}}>
                            {(pm.full_name||'').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>{pm.full_name}</div>
                            <div style={{fontSize:'11px',color:'var(--muted)'}}>{pm.email}</div>
                          </div>
                        </div>
                        <button onClick={()=>stagePmRemove(pm.user_id)}
                          style={{background:'#FEE2E2',border:'none',cursor:'pointer',color:'#DC2626',borderRadius:'8px',padding:'5px 12px',fontSize:'12px',fontWeight:'600'}}>
                          Remove
                        </button>
                      </div>
                    ))
                  }
                </div>
                <div>
                  <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'10px'}}>Add Project Manager</div>
                  <div style={{maxHeight:'240px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'4px'}}>
                    {allUsers.filter(u=>u.role!=='guest'&&!pms.find(p=>p.user_id===u.id)).map(u=>(
                      <div key={u.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 12px',borderRadius:'8px',border:'1px solid var(--border)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                          <div style={{width:30,height:30,borderRadius:'50%',background:u.avatar_color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:'11px',fontWeight:'700',flexShrink:0}}>
                            {(u.full_name||'').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                          </div>
                          <div style={{fontSize:'13px',color:'var(--text)'}}>{u.full_name}</div>
                        </div>
                        <button onClick={()=>stagePmAdd(u.id)}
                          style={{background:'#EDE9FE',border:'none',cursor:'pointer',color:'#5B5BD6',borderRadius:'8px',padding:'5px 12px',fontSize:'12px',fontWeight:'600'}}>
                          + Add as PM
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {/* Footer — changes apply immediately, so just close */}
            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',paddingTop:'16px',borderTop:'1px solid var(--border)',marginTop:'16px'}}>
              <button onClick={()=>setPmModal(null)}
                style={{padding:'8px 22px',borderRadius:'8px',border:'none',background:'#5B5BD6',cursor:'pointer',fontSize:'13px',fontWeight:'700',color:'#fff'}}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Professional window for the project creator to record the planned schedule —
// Project Start and Target End — which then feed the project's reports. The actual
// end is captured separately so a planned-vs-actual comparison can be shown here later.
function ProjectScheduleModal({ sp, onClose, onSaved }) {
  const [plannedStart, setPlannedStart] = useState('');
  const [plannedEnd, setPlannedEnd] = useState('');
  const [actualEnd, setActualEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await api.get(`/projects/${sp.id}/schedule`);
        if (!alive) return;
        setPlannedStart(s?.planned_start || '');
        setPlannedEnd(s?.planned_end || '');
        setActualEnd(s?.actual_end || '');
      } catch (e) { /* defaults */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [sp.id]);

  const save = async () => {
    setErr('');
    if (plannedStart && plannedEnd && plannedEnd < plannedStart) { setErr('Target End cannot be before Project Start.'); return; }
    setBusy(true);
    try {
      await api.put(`/projects/${sp.id}/schedule`, { planned_start: plannedStart || '', planned_end: plannedEnd || '', actual_end: actualEnd || '' });
      onSaved();
    } catch (e) { setErr(e?.error || 'Failed to save schedule'); setBusy(false); }
  };

  // Variance preview (planned target vs actual end), shown only once both are set.
  let variance = null;
  if (plannedEnd && actualEnd) {
    const d = Math.round((new Date(actualEnd) - new Date(plannedEnd)) / 86400000);
    variance = d;
  }
  const lblS = { fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'.6px', color:'var(--muted)', marginBottom:'6px', display:'block' };
  const inpS = { width:'100%', padding:'9px 11px', borderRadius:'8px', border:'1px solid var(--border)', fontSize:'13px', background:'var(--surface)', color:'var(--text)', boxSizing:'border-box' };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
      <div style={{background:'var(--surface)',borderRadius:'16px',padding:'26px',width:'460px',maxWidth:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'6px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <div style={{width:'34px',height:'34px',borderRadius:'8px',background:'rgba(3,105,161,0.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'17px'}}>📅</div>
            <div>
              <div style={{fontSize:'15px',fontWeight:'800',color:'var(--text)'}}>Project Schedule</div>
              <div style={{fontSize:'11.5px',color:'var(--muted)'}}>{sp.name}</div>
            </div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',fontSize:'19px',color:'var(--muted)',lineHeight:1}}>✕</button>
        </div>
        <p style={{fontSize:'12px',color:'var(--muted)',lineHeight:1.55,margin:'10px 0 18px'}}>
          Set the planned dates for this project. These appear as <strong style={{color:'var(--text)'}}>Project Start</strong> and <strong style={{color:'var(--text)'}}>Target End</strong> in the project reports.
        </p>

        {loading ? (
          <div style={{textAlign:'center',padding:'24px',color:'var(--muted)',fontSize:'13px'}}>Loading…</div>
        ) : (
          <>
            <div style={{display:'flex',gap:'12px',marginBottom:'14px'}}>
              <div style={{flex:1}}>
                <label style={lblS}>Project Start</label>
                <input type="date" style={inpS} value={plannedStart} onChange={e=>setPlannedStart(e.target.value)} />
              </div>
              <div style={{flex:1}}>
                <label style={lblS}>Target End</label>
                <input type="date" style={inpS} value={plannedEnd} onChange={e=>setPlannedEnd(e.target.value)} />
              </div>
            </div>
            <div style={{marginBottom:'8px'}}>
              <label style={lblS}>Actual End <span style={{textTransform:'none',fontWeight:500,color:'var(--muted)'}}>(optional — for comparison)</span></label>
              <input type="date" style={inpS} value={actualEnd} onChange={e=>setActualEnd(e.target.value)} />
            </div>

            {variance !== null && (
              <div style={{marginTop:'12px',padding:'10px 12px',borderRadius:'8px',fontSize:'12.5px',fontWeight:'600',
                background: variance > 0 ? '#FEF2F2' : '#F0FDF4', color: variance > 0 ? '#B91C1C' : '#15803D',
                border:`1px solid ${variance > 0 ? '#FECACA' : '#BBF7D0'}`}}>
                {variance === 0 ? 'Delivered exactly on the target date.'
                  : variance > 0 ? `Finished ${variance} day${variance===1?'':'s'} after the target end.`
                  : `Finished ${Math.abs(variance)} day${Math.abs(variance)===1?'':'s'} ahead of the target end.`}
              </div>
            )}
            {err && <div style={{marginTop:'12px',color:'#DC2626',fontSize:'12px',fontWeight:'600'}}>{err}</div>}

            <div style={{display:'flex',gap:'10px',justifyContent:'flex-end',marginTop:'20px',paddingTop:'16px',borderTop:'1px solid var(--border)'}}>
              <button className="btn" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save Schedule'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function QuickLink({ label, color, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <span onClick={onClick}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{fontSize:'11px',fontWeight:'600',padding:'3px 9px',borderRadius:'99px',cursor:'pointer',
        background:hov?color+'22':'var(--surface2)',color:hov?color:'var(--muted2)',
        border:'1px solid '+(hov?color+'44':'var(--border)'),transition:'all .15s'}}>
      {label}
    </span>
  );
}

function ProjectCard({sp,icon,onNavigate,onDelete,onRename,onManagePMs,onToggleVisibility,onSchedule,isOwner=false}) {
  const [hov,setHov]=useState(false);
  const [editing,setEditing]=useState(false);
  const [name,setName]=useState(sp.name);
  const col = sp.color||'#5B5BD6';
  const isPublic = sp.visibility === 'public';
  return (
    <div
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      onClick={()=>!editing&&onNavigate('/spaces/'+sp.id)}
      style={{background:'var(--surface)',border:'2px solid '+(hov?col:'var(--border)'),borderRadius:'16px',
        padding:'22px',cursor:'pointer',transition:'all .2s',boxShadow:hov?'0 8px 32px rgba(0,0,0,0.10)':'var(--shadow)'}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'14px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <div style={{width:44,height:44,borderRadius:'12px',background:col+'22',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'22px',flexShrink:0}}>{icon}</div>
          <div>
            {editing
              ? <input autoFocus className="input" value={name}
                  style={{fontSize:'15px',fontWeight:'700',padding:'3px 8px',height:'30px'}}
                  onChange={e=>setName(e.target.value)}
                  onBlur={()=>{setEditing(false);if(name.trim()&&name!==sp.name)onRename(name.trim());else setName(sp.name);}}
                  onKeyDown={e=>{if(e.key==='Enter'){setEditing(false);if(name.trim()&&name!==sp.name)onRename(name.trim());else setName(sp.name);}if(e.key==='Escape'){setEditing(false);setName(sp.name);}}}
                  onClick={e=>e.stopPropagation()}/>
              : <div style={{fontSize:'15px',fontWeight:'700',color:'var(--text)',letterSpacing:'-0.3px'}}
                  onDoubleClick={()=>setEditing(true)} title="Double-click to rename">{sp.name}</div>
            }
            <div style={{display:'flex',alignItems:'center',gap:'6px',marginTop:'2px'}}>
              <span style={{fontSize:'11px',color:'var(--muted)'}}>Project</span>
              {onToggleVisibility ? (
                <button onClick={e=>{e.stopPropagation();onToggleVisibility();}}
                  title={isPublic?'Public — click to make Private':'Private — click to make Public'}
                  style={{fontSize:'10px',fontWeight:'700',padding:'2px 9px',borderRadius:'99px',cursor:'pointer',
                    color:isPublic?'#0E7C5A':'#B4452F',
                    background:isPublic?'rgba(28,166,107,0.12)':'rgba(224,85,107,0.12)',
                    border:'1px solid '+(isPublic?'rgba(28,166,107,0.45)':'rgba(224,85,107,0.45)'),
                    display:'inline-flex',alignItems:'center',gap:'4px',transition:'all .15s'}}>
                  {isPublic?'🌐 Public':'🔒 Private'}
                  <span style={{opacity:0.6,fontSize:'9px'}}>⇄</span>
                </button>
              ) : (
                <span title={isPublic?'Visible to all workspace members (read-only)':'Only owner and invited members can see this'}
                  style={{fontSize:'10px',fontWeight:'700',padding:'2px 9px',borderRadius:'99px',
                    color:isPublic?'#0E7C5A':'#B4452F',
                    background:isPublic?'rgba(28,166,107,0.12)':'rgba(224,85,107,0.12)',
                    border:'1px solid '+(isPublic?'rgba(28,166,107,0.35)':'rgba(224,85,107,0.35)')}}>
                  {isPublic?'🌐 Public':'🔒 Private'}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{display:'flex',gap:'4px'}} onClick={e=>e.stopPropagation()}>
          <button onClick={()=>setEditing(true)} title="Rename project"
            style={{background:'none',border:'none',cursor:'pointer',color:'var(--muted)',fontSize:'13px',padding:'4px',borderRadius:'6px',opacity:hov?1:0,transition:'opacity .15s'}}>✏️</button>
          {isOwner && (
            <button onClick={()=>onDelete(sp)} title="Delete project (owner only)"
              style={{background:'none',border:'none',cursor:'pointer',color:'#DC2626',fontSize:'13px',padding:'4px',borderRadius:'6px',opacity:hov?1:0,transition:'opacity .15s'}}>🗑</button>
          )}
        </div>
      </div>
      {/* Progress bar */}
      {sp.totalTasks > 0 && (
        <div style={{marginBottom:'12px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'4px'}}>
            <span style={{fontSize:'11px',color:'var(--muted)',fontWeight:'600'}}>Overall Progress</span>
            <span style={{fontSize:'12px',fontWeight:'800',color:sp.progress>=100?'#16A34A':sp.progress>=60?'#0891B2':sp.progress>=30?'#D97706':'var(--text)'}}>{Math.round(sp.progress||0)}%</span>
          </div>
          <div style={{height:'6px',background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
            <div style={{height:'100%',width:Math.round(sp.progress||0)+'%',background:sp.progress>=100?'#16A34A':sp.progress>=60?'#0891B2':sp.progress>=30?'#D97706':'#DC2626',borderRadius:'99px',transition:'width .4s ease'}}/>
          </div>
          <div style={{fontSize:'10px',color:'var(--muted)',marginTop:'3px'}}>{sp.completedTasks||0} of {sp.totalTasks||0} tasks complete</div>
        </div>
      )}

      {/* Quick links */}
      <div style={{display:'flex',gap:'5px',flexWrap:'wrap',alignItems:'center'}} onClick={e=>e.stopPropagation()}>
        {[['Dashboard','/spaces/'+sp.id],['WBS','/spaces/'+sp.id+'/wbs'],['Gantt','/spaces/'+sp.id+'/gantt'],['Timesheet','/spaces/'+sp.id+'/timesheet']].map(([l,p])=>(
          <QuickLink key={p} label={l} color={col} onClick={e=>{e.stopPropagation();onNavigate(p);}}/>
        ))}
        {sp.owner_name&&<div style={{fontSize:'10px',color:'var(--muted)',padding:'2px 0 4px'}}>Created by <strong style={{color:'var(--text)'}}>{sp.owner_name}</strong></div>}
        {onManagePMs && (
          <button onClick={e=>{e.stopPropagation();onManagePMs();}}
            style={{fontSize:'10px',fontWeight:'700',padding:'3px 8px',borderRadius:'6px',border:'1px solid #C4B5FD',background:'#EDE9FE',color:'#5B5BD6',cursor:'pointer'}}>
            👑 PMs
          </button>
        )}
        {onSchedule && (
          <button onClick={e=>{e.stopPropagation();onSchedule();}} title="Set the planned project start and target end dates"
            style={{fontSize:'10px',fontWeight:'700',padding:'3px 8px',borderRadius:'6px',border:'1px solid #BAE6FD',background:'#E0F2FE',color:'#0369A1',cursor:'pointer'}}>
            📅 Schedule
          </button>
        )}
      </div>
    </div>
  );
}
